import { useCallback } from 'react';
import type { ImageBleed, PAGE_POSITIONS } from 'types';
import type { ImageWithMetadata } from 'types/capi/article';
import type { ProductListItemInternal } from 'types/capi/shared';

import ChevronIcon from '~/assets/icons/Chevron';
import InformationIcon from '~/assets/icons/Information';
import NotifyMeBell from '~/assets/icons/NotifyMeBell';
import AffiliatePageLink from '~/components/AffiliatePageLink';
import Image from '~/components/Image';
import ImageMetadata, { LinkType } from '~/components/ImageMetadata';
import ProductNotificationButton, {
  type NotificationData
} from '~/components/ProductNotificationButton';
import Tracked from '~/components/Tracked';
import EditorialImageImpressionEvent, {
  type ImageGridDisplayType
} from '~/events/EditorialImageImpressionEvent';
import InlineProductCardImpressionEvent from '~/events/InlineProductCardImpressionEvent';
import InlineProductCardInteractionEvent from '~/events/InlineProductCardInteractionEvent';
import ProductCardImpressionEvent from '~/events/ProductCardImpressionEvent';
import ProductCardInteractionEvent from '~/events/ProductCardInteractionEvent';
import useImpression from '~/hooks/useImpression';
import useRegion from '~/hooks/useRegion';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import { PLP_SEGMENTS } from '~/utils/navigationContext';
import { getTrackedUrlAndClickRef } from '~/utils/tracking';

const MODULE_TYPE = 'image-grid';

function useImageGridItemImpression({
  displayType,
  item,
  modulePosition,
  position
}: {
  displayType: ImageGridDisplayType;
  item: ImageWithMetadata;
  modulePosition?: number;
  position: number;
}) {
  const { isBot } = useRootData();
  const { guestId } = useUser();

  const track = useCallback(() => {
    const { product } = item;
    const shared = { displayType, guestId, isBot, modulePosition, position };

    if (product?.type === 'internal' || product?.type === 'external') {
      new ProductCardImpressionEvent({
        ...shared,
        moduleType: MODULE_TYPE,
        product
      }).track();

      return;
    }

    if (product?.type === 'inline-external') {
      new InlineProductCardImpressionEvent({
        ...shared,
        moduleType: MODULE_TYPE,
        product
      }).track();

      return;
    }

    new EditorialImageImpressionEvent({ ...shared, item }).track();
  }, [displayType, guestId, isBot, item, modulePosition, position]);

  return useImpression<HTMLDivElement>(track, { enabled: !isBot });
}

function useProductCardClick({
  displayType,
  modulePosition,
  position,
  product
}: {
  displayType: ImageGridDisplayType;
  modulePosition?: number;
  position: number;
  product: ImageWithMetadata['product'];
}) {
  const { isBot } = useRootData();
  const user = useUser();
  const { guestId } = user;

  return useCallback(() => {
    const shared = {
      displayType,
      gesture: 'click',
      guestId,
      isBot,
      modulePosition,
      moduleType: MODULE_TYPE,
      position
    };

    if (product?.type === 'inline-external') {
      // Only the affiliate branch below renders the tracked href, so only it hands the retailer a
      // clickref; the same inputs AffiliatePageLink mints from, so the same value. A card with no
      // affiliate navigates to the raw url and the event omits the field.
      const clickref = product.affiliate
        ? getTrackedUrlAndClickRef(product, { user }).clickRef
        : undefined;

      new InlineProductCardInteractionEvent({
        ...shared,
        clickref,
        product
      }).send();

      return;
    }

    if (product?.type !== 'internal' && product?.type !== 'external') return;

    new ProductCardInteractionEvent({
      ...shared,
      product,
      retailerId:
        product.type === 'external'
          ? product.affiliate?.canonical_id
          : undefined
    }).send();
  }, [displayType, guestId, isBot, modulePosition, position, product, user]);
}

export default function ImageWithMetadata({
  bleed,
  displayType,
  index,
  item,
  modulePosition,
  moduleType = MODULE_TYPE,
  pagePosition = 'Image Grid'
}: {
  bleed?: ImageBleed;
  displayType: ImageGridDisplayType;
  index: number;
  item: ImageWithMetadata;
  modulePosition?: number;
  moduleType?: string;
  pagePosition?: PAGE_POSITIONS;
}) {
  const { cta, desktop, mobile, product } = item;
  const impressionRef = useImageGridItemImpression({
    displayType,
    item,
    modulePosition,
    position: index + 1
  });
  const trackProductCardClick = useProductCardClick({
    displayType,
    modulePosition,
    position: index + 1,
    product
  });

  const productImage =
    product && 'image' in product ? product.image : undefined;
  const imageSrc = mobile.url || productImage?.url || '';
  const imageAlt = mobile.alt || productImage?.alt || `image-${index}`;
  const desktopSrc = desktop.url || productImage?.url || '';
  const linkType = cta?.path ? deriveLinkTypeFromPath(cta.path) : 'product';

  const region = useRegion();

  const showNotifyOverlay =
    region.code === 'GB' &&
    product?.type === 'internal' &&
    (product.stock_status === 'upcoming' ||
      product.stock_status === 'coming-soon');

  const renderImageBlock = (chip?: React.ReactNode) => (
    <div className="relative">
      <Image
        alt={imageAlt}
        src={imageSrc}
        display={bleed === 'full' ? 'full-bleed' : undefined}
        responsiveOptions={{
          md: { src: desktopSrc },
          xs: { src: imageSrc }
        }}
        progressiveLoadSettings={{
          highResQuality: 100,
          lowResQuality: 50
        }}
        className="w-full"
        loading="eager"
      />

      <div className="absolute inset-x-0 bottom-0 flex items-end gap-2">
        <div className="min-w-0 flex-1">
          <ResponsiveCaption
            desktopCaption={desktop.caption}
            mobileCaption={mobile.caption}
          />
        </div>
        {chip}
      </div>
    </div>
  );

  const metadata = (product || cta) && (
    <ImageMetadata
      affiliate={
        product?.type !== 'internal'
          ? (product?.affiliate ??
            (cta?.type !== 'internal' ? cta?.affiliate : undefined))
          : undefined
      }
      bleed={bleed}
      linkType={linkType}
      price={product?.price}
      title={product?.name ?? cta?.text ?? ''}
    />
  );

  if (showNotifyOverlay && product?.type === 'internal') {
    return (
      <div ref={impressionRef} className="flex flex-col gap-3.5">
        <ProductNotificationButton
          buttonStyle="overlay"
          modulePosition={modulePosition}
          moduleType={moduleType}
          pagePosition={pagePosition}
          placement="product_card"
          position={index + 1}
          product={toNotificationData(product)}
        >
          {({ isNotified, isPending }) =>
            renderImageBlock(
              <NotifyChip isNotified={isNotified} isPending={isPending} />
            )
          }
        </ProductNotificationButton>
        {metadata}
      </div>
    );
  }

  const content = (
    <div ref={impressionRef} className="flex flex-col gap-3.5">
      {renderImageBlock(product ? <SeeMoreOverlay /> : undefined)}
      {metadata}
    </div>
  );

  if (product?.type === 'internal') {
    return (
      <Tracked.InternalLink
        to={product.path}
        name={`FEATURES_${product.name}`}
        onClick={trackProductCardClick}
        pagePosition={pagePosition}
        variant="none"
      >
        {content}
      </Tracked.InternalLink>
    );
  }

  if (
    product &&
    (product.type === 'external' || product.type === 'inline-external')
  ) {
    if (product.affiliate) {
      return (
        <AffiliatePageLink
          urlTemplates={product}
          buttonName={`FEATURES_${product.name}`}
          onClick={trackProductCardClick}
          pagePosition={pagePosition}
          variant="none"
          affiliate={product.affiliate}
        >
          {content}
        </AffiliatePageLink>
      );
    }

    return (
      <Tracked.ExternalLink
        href={product.url}
        target="_blank"
        rel="sponsored nofollow noopener"
        name={`FEATURES_${product.name}`}
        onClick={trackProductCardClick}
        pagePosition={pagePosition}
        variant="none"
      >
        {content}
      </Tracked.ExternalLink>
    );
  }

  if (cta?.type === 'internal') {
    return (
      <Tracked.InternalLink
        to={cta.path}
        name={`FEATURES_${cta.text}`}
        pagePosition={pagePosition}
        variant="none"
      >
        {content}
      </Tracked.InternalLink>
    );
  }

  if (cta) {
    return (
      <AffiliatePageLink
        urlTemplates={cta}
        buttonName={`FEATURES_${cta.text}`}
        pagePosition={pagePosition}
        variant="none"
        affiliate={cta.affiliate}
      >
        {content}
      </AffiliatePageLink>
    );
  }

  return content;
}

function SeeMoreOverlay() {
  return (
    <span className="flex h-6 shrink-0 items-center gap-1 bg-black px-2 text-xs font-semibold leading-none tracking-[0.24px] text-grey-tile">
      See more
      <span className="rotate-270">
        <ChevronIcon stroke="#CFCFCF" width="8" height="8" />
      </span>
    </span>
  );
}

function NotifyChip({
  isNotified,
  isPending
}: {
  isNotified: boolean;
  isPending: boolean;
}) {
  if (isPending) {
    return (
      <span className="flex h-6 shrink-0 items-center gap-1 bg-black px-2 text-xs font-semibold leading-5 tracking-[0.24px] text-golden-syrup">
        <InformationIcon
          width={12}
          height={12}
          className="fill-golden-syrup leading-none"
          stroke="black"
        />
        Confirm email
      </span>
    );
  }

  return (
    <span className="flex h-6 shrink-0 items-center gap-1 bg-black px-2 text-xs font-semibold leading-5 tracking-[0.24px] text-purple-hola-light">
      <NotifyMeBell
        colour="#D19FF4" // purple-hola-light
        mode="light"
        variant={isNotified ? 'filled' : 'outline-thick'}
        height={12}
        width={12}
      />
      {isNotified ? 'Notified' : 'Notify me'}
    </span>
  );
}

function toNotificationData(
  product: ProductListItemInternal
): NotificationData {
  return {
    affiliateName: product.affiliate?.name,
    id: product.id,
    image: product.image,
    legacyId: product.legacy_id,
    name: product.name,
    price: product.price,
    queryID: product.queryID,
    stockStatus: product.stock_status,
    weeklyNotificationsCount: product.weekly_notifications_count
  };
}

function ResponsiveCaption({
  desktopCaption,
  mobileCaption
}: {
  desktopCaption?: string;
  mobileCaption?: string;
}) {
  if (!desktopCaption && !mobileCaption) return null;

  const captionClass =
    'pb-3 pl-3 text-sm font-semibold text-white text-shadow-outline';

  if (desktopCaption === mobileCaption || !desktopCaption) {
    return <p className={captionClass}>{mobileCaption}</p>;
  }

  if (!mobileCaption) {
    return <p className={captionClass}>{desktopCaption}</p>;
  }

  return (
    <>
      <p className={`${captionClass} md:hidden`}>{mobileCaption}</p>
      <p className={`${captionClass} hidden md:block`}>{desktopCaption}</p>
    </>
  );
}

function deriveLinkTypeFromPath(path: string): LinkType {
  const segments = path.split('/').filter(Boolean);

  if (segments.some((s) => ['features', 'articles', 'news'].includes(s)))
    return 'article';

  const plpSegmentIndex = segments.findIndex((s) => PLP_SEGMENTS.includes(s));

  if (plpSegmentIndex !== -1) {
    return segments.length - plpSegmentIndex - 1 >= 3 ? 'product' : 'plp';
  }

  return 'external';
}
